import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Combobox, ComboboxOption } from '@/components/ui/combobox'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Separator } from '@/components/ui/separator'
import { Badge } from '@/components/ui/badge'
import { Plus, Save, X, Globe, Mail, Phone, User, Hash } from 'lucide-react'
import { SpinnerIcon } from '@/components/ui/SpinnerIcon'
import { DriverMaster, CreateAliasData } from '@/types/driverMaster'
import { cn } from '@/lib/utils'

interface CreateAliasDialogProps {
  driver: DriverMaster
  open: boolean
  onClose: () => void
  onSubmit: (alias: CreateAliasData) => Promise<void>
  loading: boolean
}

const PLATFORM_OPTIONS = [
  { value: 'bolt', label: 'Bolt', color: 'bg-green-100 text-green-800' },
  { value: 'uber', label: 'Uber', color: 'bg-black text-white' },
  { value: 'sumup', label: 'SumUp', color: 'bg-blue-100 text-blue-800' },
  { value: 'friåkning', label: 'Friåkning', color: 'bg-purple-100 text-purple-800' },
  { value: 'other', label: 'Annan', color: 'bg-gray-100 text-gray-800' }
]

export function CreateAliasDialog({
  driver,
  open,
  onClose,
  onSubmit,
  loading
}: CreateAliasDialogProps) {
  const [formData, setFormData] = useState<CreateAliasData>({
    platform: ''
  })
  const [error, setError] = useState<string | null>(null)
  const [customPlatform, setCustomPlatform] = useState('')

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      setFormData({ platform: '' })
      setCustomPlatform('')
      setError(null)
    }
  }, [open])

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Determine platform name
    const platformName = formData.platform === 'other' && customPlatform.trim() 
      ? customPlatform.trim() 
      : formData.platform

    // Basic validation
    if (!platformName) {
      setError('Plattform är obligatorisk')
      return
    }

    // Check if platform already exists
    const existingPlatforms = driver.aliases.map(a => a.platform.toLowerCase())
    if (existingPlatforms.includes(platformName.toLowerCase())) {
      setError('En alias för denna plattform finns redan')
      return
    }

    // Must have at least one additional field
    const hasAdditionalField = formData.uuid || formData.email || formData.phone || formData.name
    if (!hasAdditionalField) {
      setError('Minst ett av följande fält måste fyllas i: UUID, E-post, Telefon eller Namn')
      return
    }

    // Email validation
    if (formData.email && formData.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(formData.email.trim())) {
        setError('Ogiltigt e-postformat')
        return
      }
    }

    // Phone validation
    if (formData.phone && formData.phone.trim()) {
      const phoneRegex = /^[\+]?[0-9\s\-\(\)]{8,15}$/
      if (!phoneRegex.test(formData.phone.trim())) {
        setError('Ogiltigt telefonnummer')
        return
      }
    }

    try {
      const aliasData: CreateAliasData = {
        platform: platformName,
        uuid: formData.uuid?.trim() || undefined,
        email: formData.email?.trim() || undefined,
        phone: formData.phone?.trim() || undefined,
        name: formData.name?.trim() || undefined
      }

      await onSubmit(aliasData)
    } catch (error) {
      // Error handling is done in the parent component/store
      console.error('Error creating alias:', error)
    }
  }

  // Handle input change
  const handleInputChange = (field: keyof CreateAliasData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
    
    // Clear error when user starts typing
    if (error) {
      setError(null)
    }
  }

  // Handle platform selection
  const handlePlatformChange = (value: string) => {
    handleInputChange('platform', value)
    if (value !== 'other') {
      setCustomPlatform('')
    }
  }

  // Handle cancel
  const handleCancel = () => {
    setError(null)
    onClose()
  }

  // Get platform color
  const getPlatformColor = (platform: string) => {
    const option = PLATFORM_OPTIONS.find(opt => opt.value === platform)
    return option?.color || 'bg-gray-100 text-gray-800'
  }

  if (!driver) return null

  return (
    <Dialog open={open} onOpenChange={handleCancel}>
      <DialogContent size="md" className="overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Plus className="h-5 w-5 text-blue-600" />
            <span>Lägg till plattformsalias</span>
          </DialogTitle>
          <DialogDescription>
            Lägg till en plattformsalias för att identifiera föraren på olika tjänster
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Driver Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <User className="h-4 w-4 text-blue-600" />
              <span className="font-medium text-blue-900">{driver.primaryName}</span>
            </div>
            <div className="text-sm text-blue-800">
              Nuvarande alias ({driver.aliases.length}):
              {driver.aliases.length > 0 ? (
                <div className="flex flex-wrap gap-1 mt-1">
                  {driver.aliases.map((alias, index) => (
                    <Badge key={index} className={cn("text-xs", getPlatformColor(alias.platform))}>
                      {alias.platform}
                    </Badge>
                  ))}
                </div>
              ) : (
                <span className="ml-1">Inga alias registrerade</span>
              )}
            </div>
          </div>

          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Platform Selection */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Globe className="h-4 w-4 text-gray-500" />
              <h3 className="text-lg font-medium">Plattformsinformation</h3>
            </div>

            <div className="space-y-2">
              <Label htmlFor="platform">
                Plattform <span className="text-red-500">*</span>
              </Label>
              <Combobox
                options={PLATFORM_OPTIONS}
                value={formData.platform}
                onValueChange={handlePlatformChange}
                placeholder="Välj plattform"
                searchPlaceholder="Sök plattform..."
              />
            </div>

            {/* Custom Platform Input */}
            {formData.platform === 'other' && (
              <div className="space-y-2">
                <Label htmlFor="customPlatform">
                  Annan plattform <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="customPlatform"
                  type="text"
                  placeholder="Ange plattformens namn"
                  value={customPlatform}
                  onChange={(e) => setCustomPlatform(e.target.value)}
                  disabled={loading}
                />
              </div>
            )}
          </div>

          <Separator />

          {/* Alias Details */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Hash className="h-4 w-4 text-gray-500" />
              <h3 className="text-lg font-medium">Aliasdetaljer</h3>
            </div>
            <p className="text-sm text-gray-600">
              Fyll i minst ett av följande fält för att identifiera föraren på denna plattform.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="uuid">UUID/Användar-ID</Label>
                <Input
                  id="uuid"
                  type="text"
                  placeholder="t.ex. 12345678-abcd-1234-abcd-123456789012"
                  value={formData.uuid || ''}
                  onChange={(e) => handleInputChange('uuid', e.target.value)}
                  disabled={loading}
                />
                <p className="text-xs text-gray-500">
                  Unikt ID från plattformen
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="aliasName">Namn på plattform</Label>
                <Input
                  id="aliasName"
                  type="text"
                  placeholder="Namn som används på denna plattform"
                  value={formData.name || ''}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  disabled={loading}
                />
                <p className="text-xs text-gray-500">
                  Kan skilja sig från primära namnet
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="aliasEmail">E-postadress</Label>
                <Input
                  id="aliasEmail"
                  type="email"
                  placeholder="e-post@plattform.com"
                  value={formData.email || ''}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  disabled={loading}
                />
                <p className="text-xs text-gray-500">
                  E-post registrerad på plattformen
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="aliasPhone">Telefonnummer</Label>
                <Input
                  id="aliasPhone"
                  type="tel"
                  placeholder="+46 70 123 45 67"
                  value={formData.phone || ''}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  disabled={loading}
                />
                <p className="text-xs text-gray-500">
                  Telefon registrerat på plattformen
                </p>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={loading}
            >
              <X className="h-4 w-4 mr-2" />
              Avbryt
            </Button>
            
            <Button
              type="submit"
              disabled={loading || !formData.platform}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {loading ? (
                <div className="flex items-center">
                  <SpinnerIcon size="sm" className="text-white mr-2" />
                  Skapar...
                </div>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Lägg till alias
                </>
              )}
            </Button>
          </div>

          {/* Help Text */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-yellow-900 mb-2">
              💡 Tips för aliashantering:
            </h4>
            <ul className="text-sm text-yellow-800 space-y-1">
              <li>• UUID/Användar-ID är ofta det mest pålitliga identifieringssättet</li>
              <li>• E-post och telefon kan användas för automatisk matchning</li>
              <li>• Namnet kan skilja sig från förarens primära namn</li>
              <li>• Alla fält är valfria utom plattform och minst ett identifieringsfält</li>
            </ul>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}